import {
  type CommunityPermission,
  communityRolePermissions
} from "./communityRolePermissions.gen";
import {
  CustomClaimTypes,
  EntraAppRoleGlobalAdministrator,
  isSparkRoleKey,
  MSFT_ROLE_CLAIM_TYPE,
  type SparkRoleKey,
  type UserClaim
} from "@/models";

/** Returns true if the given user is a global admin; otherwise false */
const userIsGlobalAdmin = (userClaim: UserClaim | undefined) => {
  return userClaim?.claims?.some(
    (c) =>
      c.type === MSFT_ROLE_CLAIM_TYPE &&
      c.value === EntraAppRoleGlobalAdministrator
  );
};

/** Scope for community-related permission checks; a specific community ID, or "somewhere" for any community */
type CommunityScope = number | "somewhere";

/**
 * Gets all roles the user has at a SPECIFIC community
 * @returns Array of role names the user has at the specified community
 */
const getUserRolesAtCommunity = (
  userClaims: UserClaim | undefined,
  communityId: number
): SparkRoleKey[] => {
  if (!userClaims?.claims) {
    return [];
  }

  const suffix = `|${communityId}`;

  return userClaims.claims
    .filter(
      (claim) =>
        claim.type === CustomClaimTypes.scopedRole &&
        claim.value.endsWith(suffix)
    )
    .map((claim) => {
      const lastPipeIndex = claim.value.lastIndexOf("|");
      return claim.value.substring(0, lastPipeIndex);
    })
    .filter(isSparkRoleKey);
};

/**
 * Checks if the user has ANY of the specified roles at a SPECIFIC community
 * @returns true if the user has at least one of the specified roles at the specified community
 */
const userHasAnyOfTheseRolesAtThisCommunity = (
  userClaims: UserClaim | undefined,
  roleNames: readonly SparkRoleKey[],
  communityId: number
): boolean => {
  const roles = getUserRolesAtCommunity(userClaims, communityId);
  return roleNames.some((roleName) => roles.includes(roleName));
};

/**
 * Checks if the user has ANY of the specified roles at ANY community
 * @returns true if the user has at least one of the specified roles anywhere
 */
const userHasAnyOfTheseRolesAnywhere = (
  userClaims: UserClaim | undefined,
  roleNames: readonly SparkRoleKey[]
): boolean => {
  if (!userClaims?.claims) {
    return false;
  }

  return userClaims.claims.some(
    (c) =>
      c.type === CustomClaimTypes.scopedRole &&
      roleNames.some((role) => c.value.startsWith(role + "|"))
  );
};

/**
 * Creates a permission checker function from a CommunityPermission definition.
 * This factory avoids repeating parameter type annotations across all permission functions.
 */
const makePermissionChecker = (permission: CommunityPermission) => {
  return (
    userClaims: UserClaim | undefined,
    communityId: CommunityScope
  ): boolean => {
    if (!userClaims?.claims) {
      return false;
    }
    if (permission.globalAdminCanDo && userIsGlobalAdmin(userClaims)) {
      return true;
    }
    if (communityId === "somewhere") {
      return userHasAnyOfTheseRolesAnywhere(
        userClaims,
        permission.allowedRoles
      );
    }
    return userHasAnyOfTheseRolesAtThisCommunity(
      userClaims,
      permission.allowedRoles,
      communityId
    );
  };
};

const canViewCommunitySettings = makePermissionChecker(
  communityRolePermissions.viewCommunityDetails
);

const canSetCommunityIsSpark = makePermissionChecker(
  communityRolePermissions.setCommunityIsSpark
);

const canSetCommunityAdmin = makePermissionChecker(
  communityRolePermissions.setCommunityAdmin
);

const canSetOtherRoleAssignments = makePermissionChecker(
  communityRolePermissions.setOtherRoleAssignments
);

const canModifyProspectChecklist = makePermissionChecker(
  communityRolePermissions.modifyProspectReadinessChecklistItems
);

const canViewRecordings = makePermissionChecker(
  communityRolePermissions.viewProspectRecordings
);

const canAddOrEditRecordings = makePermissionChecker(
  communityRolePermissions.addOrEditProspectRecordings
);

const canViewProspectDocuments = makePermissionChecker(
  communityRolePermissions.viewProspectDocuments
);

const canAddOrEditProspectDocuments = makePermissionChecker(
  communityRolePermissions.addOrEditProspectDocuments
);

const canGenerateLivingStory = makePermissionChecker(
  communityRolePermissions.generateLivingStory
);

const canEditLivingStory = makePermissionChecker(
  communityRolePermissions.editLivingStory
);

export {
  userIsGlobalAdmin,
  canViewCommunitySettings,
  canSetCommunityIsSpark,
  canSetCommunityAdmin,
  canSetOtherRoleAssignments,
  canModifyProspectChecklist,
  canViewRecordings,
  canAddOrEditRecordings,
  canViewProspectDocuments,
  canAddOrEditProspectDocuments,
  canGenerateLivingStory,
  canEditLivingStory
};
